import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth';
import { login } from '../api';
import { toast } from '../toast';
import { haptic } from '../haptic';

const PLAYERS = ['Armands', 'Edgars', 'Valters', 'Emīls', 'Einārs', 'Raivis', 'Edijs', 'Jānis'];

export default function LoginButton() {
  const { user, signOut, setSession } = useAuth();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const wrap = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  if (user) {
    return (
      <div ref={wrap} className="relative">
        <button
          onClick={() => setMenuOpen((v) => !v)}
          className="relative inline-flex items-center justify-center w-9 h-9 rounded-lg bg-pitch-500/15 text-pitch-400 hover:bg-pitch-500/25 active:bg-pitch-500/35 ring-1 ring-pitch-500/30"
          aria-label={`Signed in as ${user}`}
          title={user}
        >
          <UserIcon />
          <span className="absolute -bottom-0.5 -right-0.5 min-w-[14px] h-[14px] px-0.5 grid place-items-center text-[8px] font-bold leading-none rounded-full bg-pitch-400 text-ink-950">
            {user.slice(0, 1)}
          </span>
        </button>
        {menuOpen && (
          <div className="absolute right-0 mt-1 z-40 card p-2 shadow-glow w-44">
            <div className="px-3 py-2 text-xs text-dim">
              Signed in as <span className="text-chalk font-medium">{user}</span>
            </div>
            <button
              onClick={() => { setMenuOpen(false); signOut(); }}
              className="w-full text-left px-3 py-2 rounded-md hover:bg-ink-800 text-sm text-chalk"
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-ink-800 text-chalk hover:bg-ink-700 active:bg-ink-600"
        aria-label="Sign in"
        title="Sign in"
      >
        <UserIcon />
      </button>
      {open && <LoginModal onClose={() => setOpen(false)} onSuccess={(t, n) => { setSession(t, n); setOpen(false); }} />}
    </>
  );
}

function UserIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0116 0" />
    </svg>
  );
}

function LoginModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: (t: string, n: string) => void }) {
  const [name, setName] = useState(PLAYERS[0]);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await login(name, password);
      haptic.success();
      toast.success(`Signed in as ${r.name}.`);
      onSuccess(r.token, r.name);
    } catch (e) {
      haptic.error();
      setError((e as Error).message || 'Login failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-end sm:place-items-center p-0 sm:p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="card w-full sm:w-96 p-5 rounded-t-3xl sm:rounded-2xl space-y-3 ring-1 ring-pitch-500/20"
        style={{ paddingBottom: 'max(1.25rem, calc(env(safe-area-inset-bottom) + 1rem))' }}
        data-allow-select
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold">Sign in</h2>
          <button type="button" onClick={onClose} className="text-dim text-xl leading-none px-2">×</button>
        </div>
        <p className="text-xs text-dim">
          Sign in to place side bets against the other players. Browsing leaderboards, schedule, etc. works fine without an account.
        </p>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-dim">Player</span>
          <select
            value={name} onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 text-sm text-chalk focus:outline-none focus:ring-1 focus:ring-pitch-500/40"
          >
            {PLAYERS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-dim">Password</span>
          <input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            autoFocus autoComplete="current-password"
            className="mt-1 w-full bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 text-sm text-chalk focus:outline-none focus:ring-1 focus:ring-pitch-500/40 font-mono"
          />
        </label>
        {error && <div className="text-xs text-red-400">{error}</div>}
        <button
          type="submit" disabled={busy || !password}
          className="w-full h-10 rounded-lg bg-pitch-500 text-ink-950 font-semibold text-sm hover:bg-pitch-400 active:bg-pitch-600 disabled:opacity-50"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
